import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Tooltip from '../common/Tooltip'
import LoadingSpinner from '../common/LoadingSpinner'
import ScoreSwatch from './ScoreSwatch'
import MsiOnderbouwingModal from '../meetstandaard/MsiOnderbouwingModal'
import getScorePills from '../../helpers/getScorePills'
import getBenchmarkStrength from '../../helpers/dataHelpers/getBenchmarkStrength'
import { useEmptyStateContext } from '../../StateManagment/EmptyStateContext'
import useMsiBenchmark from '../../hooks/useMsiBenchmark'
import useMsiOnderbouwing from '../../hooks/useMsiOnderbouwing'
import { useMsiEffectValueById } from '../../hooks/useMsiEffectValue'
import useOnderbouwingForEffect, { onderbouwingAvailable } from '../../hooks/useOnderbouwingForEffect'
import formatEuro from '../../helpers/formatEuro'
import formatEuroSigned from '../../helpers/formatEuroSigned'
import notAttributedText from '../../helpers/msiMonetisationHelpers/notAttributedText'
import { client } from '../../hooks/Client'

// AI-CONTEXT: See docs/features/analytics-card-redesign.md
//
// Unified score block above effect-indicator charts. Replaces the flat grey
// `GrowthAndBenchmark` strip with one block that reads the measured result
// (solid teal) and the cross-company benchmark (coral).
//
//   ┌ HERO ─────────────────────────────────────────────┐
//   │ ▬ Jouw resultaat · totale groei   +43.8%   [pill]  │
//   ├ REFS ─────────────────────────────────────────────┤
//   │ Benchmark  +24.0%                                  │
//   └───────────────────────────────────────────────────┘
//
// Same props as GrowthAndBenchmark so it drops into the chart components.
// The benchmark ref + pill are internal steering info, gated on the
// EmptyStateContext (provided on dashboard + Analyse cards, absent on public
// LiveReports/print). When there is no measured result yet the hero shows a
// placeholder and only the ref carries data.

const round1 = (v) => Math.round(v * 10) / 10
const isNum = (v) => typeof v === 'number' && Number.isFinite(v)
const fmt = (v, positivePrefix = true) => `${positivePrefix && v > 0 ? '+' : ''}${round1(v)}%`

/**
 * @param {object} props
 * @param {number | null} props.totalGrowth - Measured growth %; null when there is no response data yet
 * @param {string} [props.effectId] - OutputEffects.ID; if missing, no benchmark or monetisation is fetched
 * @param {object | null} [props.benchmark] - Pre-fetched msiBenchmark result; when provided the component does not fetch
 * @param {boolean} [props.benchmarkLoading] - Loading state of a parent-owned benchmark fetch; only used when `benchmark` is passed in
 * @param {'weak' | 'missing' | null} [props.benchmarkUnavailableReason] - Why no benchmark steering value is shown
 * @param {boolean} [props.positivePrefix] - Prefix positive values with '+' (default true)
 */
const ScoreBlock = ({ totalGrowth, effectId, benchmark: benchmarkProp, benchmarkLoading = false, benchmarkUnavailableReason: benchmarkUnavailableReasonProp = null, positivePrefix = true }) => {
    const history = useHistory()

    // Same gate the empty-state CTA uses: present on dashboard + Analyse cards,
    // absent on public LiveReports/print. The benchmark is internal steering
    // info and must not render publicly.
    const { showCta: onInternalCard } = useEmptyStateContext()

    // Parent may pass the benchmark (fetched once and shared); otherwise fetch
    // our own. Passing '' to the hook skips its fetch.
    const usingProp = benchmarkProp !== undefined
    const { benchmark: fetchedBenchmark, loading: fetchedLoading, benchmarkUnavailableReason: fetchedBenchmarkUnavailableReason } = useMsiBenchmark(usingProp ? '' : effectId)
    const benchmark = usingProp ? benchmarkProp : fetchedBenchmark
    // When the parent owns the fetch it also owns the loading flag; otherwise
    // fall back to our own hook's loading state.
    const loading = usingProp ? benchmarkLoading : fetchedLoading
    const benchmarkUnavailableReason = usingProp ? benchmarkUnavailableReasonProp : fetchedBenchmarkUnavailableReason

    const hasGrowth = isNum(totalGrowth)
    const benchGrowth = isNum(benchmark?.benchmarkGrowth) ? benchmark.benchmarkGrowth : null
    const showBenchmarkRef = effectId && benchmarkUnavailableReason !== 'weak'

    // MSI monetisation — internal steering info only: pass '' on public cards
    // so the hook skips its fetch entirely. Excluded (Participatieladder) and
    // no-amounts effects show no euro values at all.
    const { value: msiValue, scores: msiScores, error: msiError, msiId, effectName } = useMsiEffectValueById(onInternalCard && effectId ? effectId : '')
    const msiUsable = onInternalCard && msiValue && !msiValue.excluded && !msiValue.noScores
    const realMoney = msiUsable && msiValue.momentCount >= 2 ? msiValue.deltaTotal : null
    const stateMoney = msiUsable && msiValue.momentCount === 1 ? msiValue.stateValue : null
    // Rekenregels MSI: wat er van het bruto verschil aan de interventie wordt
    // toegerekend — dat is het getal op de kaart. Kon niemand doorgerekend
    // worden, dan geen €0 maar die melding.
    // AI-CONTEXT: See docs/features/msi-rekenregels-monetarisering.md
    const notAttributed = realMoney !== null ? (msiValue.notAttributedCount || 0) : 0
    const anyAttributed = realMoney !== null && (
        (msiValue.perPersona || []).some((p) => p.attributed !== null && p.attributed !== undefined)
        || (msiValue.anonymous && msiValue.anonymous.attributed !== null && msiValue.anonymous.attributed !== undefined)
    )

    // Onderbouwing behind the euro value (meetstandaard monetarisering-
    // document). Fails visibly but never blocks the euro display: without an
    // entry the ⓘ simply does not render. Public cards skip the fetch.
    const { index: onderbouwingIndex, meta: onderbouwingMeta } = useMsiOnderbouwing(!!(onInternalCard && effectId))
    const [onderbouwingOpen, setOnderbouwingOpen] = useState(false)

    // A card that gets re-pointed at another effect must not keep the previous
    // effect's modal open (stale-derived-state family).
    useEffect(() => setOnderbouwingOpen(false), [effectId])
    // A versioned effect is explained from the document it is valued with;
    // a legacy one from the demo onderbouwing (useOnderbouwingForEffect).
    const effectRef = { msiId: msiId || '', name: effectName, standaardVersion: msiValue?.standaardVersion || null }
    const hasOnderbouwing = (realMoney !== null || stateMoney !== null)
        && onderbouwingAvailable(onderbouwingIndex, effectRef)
    const onderbouwing = useOnderbouwingForEffect(onderbouwingOpen && hasOnderbouwing ? effectRef : null, onderbouwingIndex, onderbouwingMeta)

    const onderbouwingButton = hasOnderbouwing && (
        <button
            type='button'
            className='score-block-money-info-button'
            aria-label='Onderbouwing'
            onClick={() => setOnderbouwingOpen(true)}
        >
            <InfoOutlinedIcon fontSize='inherit' />
        </button>
    )

    const pills = getScorePills({ result: hasGrowth ? totalGrowth : null, bench: benchGrowth })

    const benchmarkStrength = benchGrowth != null
        ? getBenchmarkStrength({
            companyCount: benchmark.companyCount,
            respondentCount: benchmark.respondentCount,
            respondentsPerCompany: benchmark.respondentsPerCompany,
        })
        : null

    const benchmarkTooltip = benchmarkStrength && benchmark
        ? `${benchmarkStrength.label} (${benchmarkStrength.finalScore + 1}/5). ${benchmarkStrength.helperPrefix}: ${benchmark.companyCount} ${benchmark.companyCount === 1 ? 'organisatie' : 'organisaties'}, ${benchmark.respondentCount} ${benchmark.respondentCount === 1 ? 'respondent' : 'respondenten'}, grootste organisatie ${benchmarkStrength.largestShare}%, ${benchmarkStrength.spreadLabel}.`
        : ''

    // Clicking the benchmark opens its detail page, same as before the merge.
    // Routes on the MSI id (returned by msiBenchmark), not the OutputEffects.ID.
    const benchmarkMsiId = benchmark?.msiId
    const openBenchmarkDetails = () => {
        if (benchmarkMsiId) {
            history.push(`/${client}/benchmarkdetails/${benchmarkMsiId}`)
        }
    }

    // Public (no provider): keep the old behaviour — only the bare "totale
    // groei" hero line, no benchmark ref or pill.
    const showRefs = onInternalCard && !!effectId

    return (
        <div className="score-block">
            <div className="score-block-hero">
                <ScoreSwatch kind="result" width={26} />
                <div className="score-block-hero-text">
                    <div className="score-block-label">Jouw resultaat · totale groei</div>
                    <div className={`score-block-value ${hasGrowth ? '' : 'score-block-value--empty'}`}>
                        {hasGrowth ? fmt(totalGrowth, positivePrefix) : 'Nog geen meting'}
                    </div>
                    {/* #343: het hoofdgetal is toegerekend (impactclaim, looptijd,
                        disconto); het bruto verschil per jaar staat niet meer op de kaart. */}
                    {realMoney !== null && (
                        <div className="score-block-money">
                            <span className="score-block-money-label">Maatschappelijke waarde</span>
                            {anyAttributed ? (
                                <>
                                    <span className="score-block-money-value">{formatEuroSigned(msiValue.attributedTotal)}</span>
                                    {onderbouwingButton}
                                    {notAttributed > 0 && (
                                        <span className="score-block-money-note">{notAttributedText(notAttributed, msiValue.notAttributedReasons)}</span>
                                    )}
                                </>
                            ) : (
                                <span className="score-block-money-note">{notAttributedText(null, msiValue.notAttributedReasons)}</span>
                            )}
                        </div>
                    )}
                    {stateMoney !== null && (
                        <div className="score-block-money">
                            <span className="score-block-money-label">Huidige situatiewaarde</span>
                            <span className="score-block-money-value">{formatEuro(stateMoney)}</span>
                            <span className="score-block-money-note">op basis van één meetmoment</span>
                            {onderbouwingButton}
                        </div>
                    )}
                    {onInternalCard && msiError && (
                        <div className="score-block-money score-block-money--error">
                            Maatschappelijke waarde niet geladen
                        </div>
                    )}
                </div>
                {onInternalCard && pills.length > 0 && (
                    <div className="score-block-pills">
                        {pills.map((pill) => (
                            <span key={pill.label} className={`score-block-pill score-block-pill--${pill.kind}${pill.positive ? ' score-block-pill--bench-positive' : ''}`}>
                                {pill.label}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {showRefs && (
                <div className="score-block-refs">
                    {/* Benchmark — a growth figure, not a chart line, so it gets
                        no swatch at all. */}
                    {showBenchmarkRef && (
                        <div
                            className={`score-block-ref ${benchGrowth != null ? '' : 'score-block-ref--empty'}${benchmarkMsiId ? ' score-block-ref--clickable' : ''}`}
                            onClick={benchmarkMsiId ? openBenchmarkDetails : undefined}
                            role={benchmarkMsiId ? 'button' : undefined}
                        >
                            <div className="score-block-ref-grow">
                                <div className="score-block-ref-label">Benchmark</div>

                                {loading && (
                                    <div className="score-block-ref-value score-block-ref-value--empty">
                                        <LoadingSpinner className="loading-spinner--inline" />
                                    </div>
                                )}

                                {!loading && benchGrowth == null && (
                                    <div className="score-block-ref-value score-block-ref-value--empty">
                                        {benchmarkUnavailableReason === 'weak' ? 'onvoldoende benchmark' : 'geen data'}
                                    </div>
                                )}

                                {!loading && benchGrowth != null && (
                                    <>
                                        <div className="score-block-ref-value">{fmt(benchGrowth, positivePrefix)}</div>
                                        {benchmarkStrength && (
                                            <div className="score-block-ref-meta">{benchmarkStrength.label}</div>
                                        )}
                                    </>
                                )}
                            </div>

                            {!loading && benchGrowth != null && benchmarkStrength && (
                                <Tooltip content={benchmarkTooltip} top="30px" width="20px">
                                    <InfoOutlinedIcon className="score-block-ref-info" />
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>
            )}

            <MsiOnderbouwingModal
                entry={onderbouwing.entry}
                meta={onderbouwing.meta}
                loading={onderbouwing.loading}
                error={onderbouwing.error}
                effectName={onderbouwing.effectName}
                msiScores={msiScores}
                onClose={() => setOnderbouwingOpen(false)}
            />
        </div>
    )
}

export default ScoreBlock
